import { flags } from '@/assets';
import { findPhoneCountry } from './phoneCountries';

export type CountryCode = 'uy' | 'pe';

export const COUNTRY_CODES: readonly CountryCode[] = ['uy', 'pe'];

const isCountryCode = (value: string): value is CountryCode =>
  (COUNTRY_CODES as readonly string[]).includes(value);

/**
 * País de ESTE build. Sale de `VITE_COUNTRY`, que vite inyecta a partir del
 * modo (`production.pe` -> `pe`).
 *
 * El fallback lee el modo directo porque hasta ahora el país se deducía así en
 * `EnvFlag`, y los builds viejos que todavía no tengan `VITE_COUNTRY` siguen
 * resolviendo bien.
 */
const resolveCountry = (): CountryCode => {
  const explicit = String(import.meta.env.VITE_COUNTRY ?? '').toLowerCase();
  if (isCountryCode(explicit)) return explicit;

  const rawMode = String(import.meta.env.VITE_APP_ENV || import.meta.env.MODE || '');
  const fromMode = rawMode.split('.')[1]?.toLowerCase() ?? '';
  return isCountryCode(fromMode) ? fromMode : 'uy';
};

export const COUNTRY: CountryCode = resolveCountry();

/** Perú no tiene ATC: los casos se ocultan en vez de mostrar errores. */
export const ATC_AVAILABLE = COUNTRY !== 'pe';

export const COUNTRY_LABEL: Record<CountryCode, string> = {
  uy: 'Uruguay',
  pe: 'Perú',
};

export const COUNTRY_FLAG: Record<CountryCode, string> = {
  uy: flags.uy,
  pe: flags.pe,
};

/**
 * Id del país en la tabla `countries` de la base, para los campos
 * `originCountryId` / `destinationCountryId` del alta de pedidos.
 *
 * Cada país tiene su propia base, pero las dos traen la región entera en
 * `countries`, así que mandar el id de Uruguay desde el panel peruano NO da
 * error de clave foránea: guarda el pedido con el país equivocado y no se
 * entera nadie. Por eso sale del país del build y no de una constante suelta.
 *
 * Se puede pisar con `VITE_COUNTRY_ID` para un entorno cuya fila tenga otro
 * id (una base de test recreada desde cero, por ejemplo).
 */
const DEFAULT_COUNTRY_ID: Record<CountryCode, string> = {
  uy: 'a5a7e8f1-23e6-4b87-a4d2-fa5408fc2be2',
  pe: '05d9943d-273c-40d8-ae1d-f13a17e70bcf',
};

export const countryId = (code: CountryCode): string =>
  DEFAULT_COUNTRY_ID[code];

export const COUNTRY_ID: string =
  String(import.meta.env.VITE_COUNTRY_ID ?? '').trim() || countryId(COUNTRY);

/** El otro país, que es al que apunta el botón de cambio. */
export const OTHER_COUNTRY: CountryCode = COUNTRY === 'uy' ? 'pe' : 'uy';

/**
 * Dónde vive el panel de cada país.
 *
 * Uruguay se sirve en la raíz del dominio y Perú bajo `/pe/` (ver
 * docs/CLOUDFRONT_RUTAS.md). Son el MISMO origen a propósito: por eso el
 * `localStorage` se comparte y la sesión sobrevive al cambio de país sin
 * volver a loguearse.
 *
 * Se pueden pisar con `VITE_PANEL_URL_UY` / `VITE_PANEL_URL_PE` para apuntar a
 * otro dominio o para probar en local, donde `/pe/` no existe.
 */
const DEFAULT_PANEL_PATH: Record<CountryCode, string> = {
  uy: '/',
  pe: '/pe/',
};

export const countryPanelUrl = (code: CountryCode): string => {
  const override = String(
    code === 'uy'
      ? (import.meta.env.VITE_PANEL_URL_UY ?? '')
      : (import.meta.env.VITE_PANEL_URL_PE ?? '')
  ).trim();
  return override || DEFAULT_PANEL_PATH[code];
};

/**
 * ¿El cambio de país mantiene la sesión?
 *
 * Solo si el panel destino está en el mismo origen: el token vive en el
 * `localStorage`, que es por origen. Si alguien configura un dominio aparte
 * hay que volver a loguearse, y conviene avisarlo en vez de prometer lo que no
 * se cumple.
 */
export const keepsSessionOnSwitch = (code: CountryCode): boolean => {
  const url = countryPanelUrl(code);
  if (url.startsWith('/')) return true;
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * País donde parece estar el visitante, por zona horaria del navegador.
 *
 * Se usa la zona horaria y no la geolocalización del navegador a propósito:
 * `navigator.geolocation` abre un cartel de permiso —que en una pantalla de
 * login la mayoría rechaza— y no responde si lo deniegan. La zona horaria no
 * pide permiso, no sale a la red y alcanza para distinguir Lima de Montevideo.
 *
 * Devuelve null cuando no se puede afirmar nada: ahí no se toca nada y el
 * visitante se queda donde entró.
 */
const TIMEZONE_COUNTRY: Record<string, CountryCode> = {
  'america/lima': 'pe',
  'america/montevideo': 'uy',
};

export const detectVisitorCountry = (): CountryCode | null => {
  try {
    const zone = Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone?.toLowerCase();
    if (!zone) return null;
    return TIMEZONE_COUNTRY[zone] ?? null;
  } catch {
    return null;
  }
};

/**
 * Zona horaria de cada país.
 *
 * Las horas de las vueltas de retiro (`pickup_cycles.cycle_time`) son horas de
 * pared del país donde corre el back, sin zona: las 09:00 de Perú son las 9 de
 * la mañana en Lima, las mire quien las mire. El panel las muestra tal cual, y
 * por eso hace falta decir de qué país son: alguien en Montevideo que lee
 * "09:00" está viendo un retiro que en su reloj pasa a las 11.
 */
const TIMEZONE_BY_COUNTRY: Record<CountryCode, string> = {
  uy: 'America/Montevideo',
  pe: 'America/Lima',
};

export const COUNTRY_TIMEZONE: string = TIMEZONE_BY_COUNTRY[COUNTRY];

/** Milisegundos a sumarle a UTC para llegar a la hora de pared de `timeZone`. */
const zoneOffsetMs = (instant: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value);

  const hour = read('hour') === 24 ? 0 : read('hour');
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second')
  );

  return asIfUtc - instant.getTime();
};

/**
 * La misma hora, leída desde otro país: `wallClockInCountry('09:00', 'pe', 'uy')`
 * devuelve `'11:00'`.
 *
 * Se ancla en el día de hoy porque el desfase entre dos zonas no es una
 * constante: hoy Lima y Montevideo van dos horas, pero cualquiera de los dos
 * podría volver al horario de verano y pasarían a una. Restar un número fijo
 * daría la hora equivocada el día que eso cambie.
 */
export const wallClockInCountry = (
  time: string,
  from: CountryCode,
  to: CountryCode
): string => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time ?? '').trim());
  if (!match) return '';
  if (from === to) return time;

  const [hours, minutes] = [Number(match[1]), Number(match[2])];
  const today = new Date();
  const asUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    hours,
    minutes
  );

  const fromZone = TIMEZONE_BY_COUNTRY[from];
  const toZone = TIMEZONE_BY_COUNTRY[to];

  const instant = new Date(asUtc - zoneOffsetMs(new Date(asUtc), fromZone));
  const shifted = new Date(instant.getTime() + zoneOffsetMs(instant, toZone));

  const label = `${String(shifted.getUTCHours()).padStart(2, '0')}:${String(
    shifted.getUTCMinutes()
  ).padStart(2, '0')}`;

  /*
   * Una vuelta de las 23:30 en Perú cae a la 01:30 del dia siguiente en
   * Uruguay. Decir solo "01:30" haria pensar que es la madrugada del mismo dia.
   */
  const dayShift = Math.round(
    (Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate()
    ) -
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      86400000
  );

  if (dayShift > 0) return `${label} del día siguiente`;
  if (dayShift < 0) return `${label} del día anterior`;
  return label;
};

/**
 * Un instante formateado en la hora de un país.
 *
 * Se usa para los momentos que el sistema guarda en UTC —cuándo corrió algo,
 * cuándo vuelve a correr—. Formatearlos con la zona del navegador, que es lo
 * que hace `dayjs` por defecto, le muestra a cada uno una hora distinta para
 * el mismo evento y sin decir cuál es: alguien en Montevideo y alguien en Lima
 * leen la misma fila con dos horas separadas por dos horas.
 */
export const formatInstantInCountry = (
  value: Date | string | number | null | undefined,
  code: CountryCode = COUNTRY,
  options?: { withDate?: boolean }
): string => {
  if (value === null || value === undefined || value === '') return '';

  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return '';

  const withDate = options?.withDate ?? true;

  return new Intl.DateTimeFormat('es-UY', {
    timeZone: TIMEZONE_BY_COUNTRY[code],
    hour12: false,
    ...(withDate
      ? { day: '2-digit', month: '2-digit', year: '2-digit' }
      : {}),
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(instant);
};

/**
 * Qué hora local de un país es una hora UTC dada: `utcTimeInCountry(14, 0, 'pe')`
 * devuelve `'09:00'`.
 *
 * Hace falta donde se escriben horarios en UTC —las expresiones cron del
 * scheduler— para poder mostrar al lado a qué hora de la operación equivalen.
 */
export const utcTimeInCountry = (
  hour: number,
  minute: number,
  code: CountryCode
): string => {
  const today = new Date();
  const instant = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      hour,
      minute
    )
  );

  return new Intl.DateTimeFormat('es-UY', {
    timeZone: TIMEZONE_BY_COUNTRY[code],
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(instant);
};

/**
 * Cómo se escribe un celular en cada país.
 *
 * El panel pide SOLO los dígitos que van después del prefijo fijo y arma el
 * número completo al enviar: en Uruguay el usuario tipea 7 dígitos y el form
 * antepone `09`; en Perú tipea 8 y se antepone `9`. Hasta ahora el `09` estaba
 * escrito a mano en los formularios de invitación, así que el panel peruano
 * guardaba celulares de 9 dígitos que empezaban con 09 y no existían.
 *
 * `countryCode` es el prefijo internacional (sin `+`) que se usa para armar el
 * link de WhatsApp.
 */
export interface MobilePhoneConvention {
  /** Prefijo fijo que el usuario NO escribe; se muestra como adorno del input. */
  prefix: string;
  /** Cantidad de dígitos que sí escribe el usuario. */
  suffixLength: number;
  /** Prefijo internacional sin `+`. */
  countryCode: string;
}

const buildMobilePhoneConvention = (
  code: CountryCode
): MobilePhoneConvention => {
  const country = findPhoneCountry(code)!;
  return {
    prefix: `${country.trunkPrefix}${country.mobilePrefix}`,
    suffixLength: country.length,
    countryCode: country.dialCode,
  };
};

export const MOBILE_PHONE: MobilePhoneConvention =
  buildMobilePhoneConvention(COUNTRY);

/** Largo total del celular local, ya con el prefijo fijo. */
export const MOBILE_PHONE_LENGTH: number =
  MOBILE_PHONE.prefix.length + MOBILE_PHONE.suffixLength;
